/**
 * Payment-method columns for the clinic-level receipt report (ใบเสร็จ).
 *
 * Payment methods are per-branch master rows (m_receipt_payment.branch_id), so the
 * same "เงินสด" has a different id in every branch. Across branches they are grouped
 * by NAME. Column keys are synthetic ("pm_<n>") because b-table treats dots in a
 * field key as a path and payment names can contain anything.
 */

const UNNAMED = "ไม่ระบุ";

const round2 = n => Math.round(n * 100) / 100;

const methodName = p => String((p && p.name) || "").trim() || UNNAMED;

/** name -> summed amount for one receipt's receiptPayment list. */
function amountsByName(receipt) {
  const map = new Map();
  for (const p of (receipt && receipt.receiptPayment) || []) {
    const amount = Number(p.amount);
    if (isNaN(amount)) continue;
    const name = methodName(p);
    map.set(name, round2((map.get(name) || 0) + amount));
  }
  return map;
}

/**
 * [{ key, name }] for every payment method that took money in `receipts`,
 * ordered by total amount (largest first), then by name.
 */
export function paymentMethodColumns(receipts) {
  const totals = new Map();
  for (const r of receipts || []) {
    for (const [name, amount] of amountsByName(r)) {
      totals.set(name, round2((totals.get(name) || 0) + amount));
    }
  }
  return [...totals.entries()]
    .filter(([, total]) => total !== 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "th"))
    .map(([name], i) => ({ key: `pm_${i}`, name }));
}

/** Copy of `receipt` with a numeric amount under each column's key (0 when unused). */
export function withPaymentAmounts(receipt, columns) {
  const amounts = amountsByName(receipt);
  const row = { ...receipt };
  for (const c of columns) row[c.key] = amounts.get(c.name) || 0;
  return row;
}
